import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {take} from "rxjs/operators";
import {AppsGroupsFiles, NetscopeApiService, Success} from "@app/services/netscope-api.service";

export class AppsGroupsFileItem {
  name: string;
  generated: boolean;
  // Unknown until the file is loaded
  groupsCount?: number;
}

/**
 * Apps groups files of the side of the page: the ones of the user first, then the ones generated by DC Netscope,
 * which are read-only.
 */
@Component({
  standalone: false,
  selector: 'app-apps-groups-files-nav',
  templateUrl: './apps-groups-files-nav.component.html',
  styleUrls: ['./apps-groups-files-nav.component.css']
})
export class AppsGroupsFilesNavComponent implements OnInit {

  @Input() selectedFile: string;
  @Output() selectedFileChange: EventEmitter<string> = new EventEmitter();
  // Emitted once the files are known, so that the page knows whether the selected one is generated
  @Output() filesLoaded: EventEmitter<AppsGroupsFileItem[]> = new EventEmitter();

  files: AppsGroupsFileItem[] = [];
  failureMode = false;

  creatingFile = false;
  newFileName = "";
  newFileNameError: string;

  constructor(private netscopeService: NetscopeApiService) {
  }

  get userFiles(): AppsGroupsFileItem[] {
    return this.files.filter((file) => !file.generated);
  }

  get generatedFiles(): AppsGroupsFileItem[] {
    return this.files.filter((file) => file.generated);
  }

  ngOnInit(): void {
    this.reloadFiles();
  }

  reloadFiles = () => {
    this.netscopeService.getAppsGroupsFiles().subscribe((response) => {
      if (!(response instanceof Success)) {
        this.failureMode = true;
        return;
      }

      let appsGroupsFiles: AppsGroupsFiles = response.element;
      let previousCounts = new Map(this.files.map((file) => [file.name, file.groupsCount]));
      this.files = appsGroupsFiles.files.map((name) => ({
        name: name,
        generated: appsGroupsFiles.generatedFiles.includes(name),
        groupsCount: previousCounts.get(name),
      }));
      this.failureMode = false;
      this.filesLoaded.emit(this.files);
      this.loadGroupsCounts();
    });
  }

  // The API gives no count with the files: each file is fetched for it
  private loadGroupsCounts() {
    for (let file of this.files) {
      this.netscopeService.getAppsGroups(file.name).pipe(take(1)).subscribe((response) => {
        if (response instanceof Success) {
          file.groupsCount = (response.element?.apps_groups ?? []).length;
        }
      });
    }
  }

  // Called by the page once it saved a file, whose groups count may have changed
  setGroupsCount = (fileName: string, groupsCount: number) => {
    let file = this.files.find((candidate) => candidate.name == fileName);
    if (file != undefined) {
      file.groupsCount = groupsCount;
    }
  }

  selectFile = (file: AppsGroupsFileItem) => {
    if (file.name == this.selectedFile) {
      return;
    }
    this.selectedFileChange.emit(file.name);
  }

  startFileCreation = () => {
    this.creatingFile = true;
    this.newFileName = "";
    this.newFileNameError = undefined;
  }

  cancelFileCreation = () => {
    this.creatingFile = false;
  }

  createFile = () => {
    let name = this.newFileName.trim();

    // The same characters as the ones accepted by the API
    if (!/^[A-Za-z0-9_-]+$/.test(name)) {
      this.newFileNameError = "Only letters, digits, '_' and '-'.";
      return;
    }
    if (this.files.some((file) => file.name == name)) {
      this.newFileNameError = "A file already has this name.";
      return;
    }

    this.netscopeService.createAppsGroupsFile(name).subscribe((response) => {
      if (!(response instanceof Success)) {
        this.newFileNameError = "The file could not be created.";
        return;
      }

      this.creatingFile = false;
      this.reloadFiles();
      this.selectedFileChange.emit(name);
    });
  }
}
